import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles,
  Loader2,
  CheckCircle2,
  X,
  Trash2,
  Trophy,
  Route,
  User,
  MapPin,
  Clock,
  AlertTriangle,
  Ban,
  Search,
  NotebookPen,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

function ConstitutionBlock() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const toggle = async () => {
    const opening = !open;
    setOpen(opening);
    if (opening && !loaded) {
      try {
        const { data } = await api.get("/kai-planner/notes");
        setText(data?.text || "");
        setLoaded(true);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/kai-planner/notes", { text });
      setDirty(false);
      toast.success("Constitución guardada — Kai cumplirá estas normas al planificar.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-amber-200/80 bg-amber-50/40" data-testid="kai-constitution-block">
      <button type="button" onClick={toggle}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
        data-testid="kai-constitution-toggle">
        <NotebookPen className="h-4 w-4 text-orange-500" />
        <span className="text-sm font-bold text-neutral-800">Constitución del planificador</span>
        <span className="text-[11px] text-neutral-500 hidden sm:inline">— normas generales numeradas que Kai debe cumplir</span>
        {dirty && <span className="text-[10px] text-amber-700 font-semibold">(sin guardar)</span>}
        {open ? <ChevronUp className="h-4 w-4 ml-auto text-neutral-400" /> : <ChevronDown className="h-4 w-4 ml-auto text-neutral-400" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <textarea
            value={text}
            onChange={(e) => { setText(e.target.value); setDirty(true); }}
            rows={12}
            className="w-full rounded-xl border border-neutral-200 bg-white p-2.5 text-xs font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-orange-500"
            data-testid="kai-constitution-text"
          />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[10px] text-neutral-500">
              Añade, edita o elimina normas numeradas. Ej.: «13. No poner clases después de las 22:00 salvo validación del profesor.»
            </span>
            <button type="button" onClick={save} disabled={saving || !dirty}
              className="btn-secondary !h-8 !px-3 text-xs disabled:opacity-50" data-testid="kai-constitution-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Guardar constitución
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const statusLabels = {
  pending: { label: "Pendientes", cls: "border-neutral-300 bg-neutral-100" },
  accepted: { label: "Aceptadas", cls: "border-emerald-300 bg-emerald-50" },
  rejected: { label: "Rechazadas", cls: "border-red-300 bg-red-50" },
};

function ScoreBadge({ score }) {
  const s = Math.max(0, Math.min(100, score || 0));
  const cls =
    s >= 90 ? "bg-emerald-500 text-white" :
    s >= 75 ? "bg-emerald-100 text-emerald-800" :
    s >= 60 ? "bg-amber-100 text-amber-800" :
              "bg-red-100 text-red-800";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${cls}`}>
      <Trophy className="h-3 w-3" /> {s}
    </span>
  );
}

export default function PlannerTab() {
  const [drafts, setDrafts] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        api.get("/planner/drafts", { params: statusFilter === "all" ? {} : { status: statusFilter } }),
        api.get("/students"),
      ]);
      setDrafts(Array.isArray(d.data) ? d.data : []);
      setStudents(Array.isArray(s.data) ? s.data : []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const studentsById = useMemo(() => Object.fromEntries(students.map((s) => [s.id, s])), [students]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return drafts
      .filter((d) => {
        if (!q) return true;
        const st = studentsById[d.student_id];
        return (st?.name || "").toLowerCase().includes(q) ||
               (d.zone || "").toLowerCase().includes(q) ||
               (d.teacher_name || "").toLowerCase().includes(q);
      })
      .sort((a, b) => {
        if (a.status === "pending" && b.status !== "pending") return -1;
        if (b.status === "pending" && a.status !== "pending") return 1;
        return (b.score || 0) - (a.score || 0);
      });
  }, [drafts, search, studentsById]);

  // Group by student when many drafts
  const byStudent = useMemo(() => {
    const m = new Map();
    for (const d of filtered) {
      const arr = m.get(d.student_id) || [];
      arr.push(d);
      m.set(d.student_id, arr);
    }
    return Array.from(m.entries());
  }, [filtered]);

  const accept = async (d) => {
    if (!window.confirm(`¿Crear la cita real del ${d.date} ${d.time}?`)) return;
    setSaving(true);
    try {
      await api.post(`/planner/drafts/${d.id}/accept`);
      toast.success("Cita creada desde propuesta IA.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const reject = async (d) => {
    setSaving(true);
    try { await api.post(`/planner/drafts/${d.id}/reject`); await load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (d) => {
    if (!window.confirm("¿Eliminar borrador de forma permanente?")) return;
    setSaving(true);
    try { await api.delete(`/planner/drafts/${d.id}`); await load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="planner-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-orange-500" /> Kai Planificador
          </h2>
          <p className="text-xs text-neutral-500 mt-0.5">
            Propuestas generadas por Kai para todos los alumnos. <b>Kai propone, tú decides.</b>
          </p>
        </div>
      </div>

      <ConstitutionBlock />

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-xl border border-neutral-200 bg-white p-0.5" data-testid="planner-status-filter">
          {["pending", "accepted", "rejected", "all"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={"h-8 px-3 rounded-lg text-xs font-bold transition-colors " + (statusFilter === s ? "bg-orange-500 text-white" : "text-neutral-600 hover:bg-neutral-100")}
              data-testid={`planner-filter-${s}`}
            >
              {s === "all" ? "Todas" : statusLabels[s].label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
          <input
            type="text"
            placeholder="Buscar por alumno, zona o profesor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-9 rounded-xl border border-neutral-200 bg-white pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            data-testid="planner-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-neutral-500"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Cargando…</div>
      ) : byStudent.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-10 text-center">
          <Sparkles className="h-8 w-8 text-orange-400 mx-auto mb-2" />
          <div className="text-sm font-semibold text-neutral-700">Sin propuestas {statusFilter !== "all" ? statusLabels[statusFilter].label.toLowerCase() : ""}</div>
          <p className="text-xs text-neutral-500 mt-1">Abre un alumno → pestaña Horarios → botón "Sugerir próxima clase" para generar propuestas.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {byStudent.map(([studentId, list]) => {
            const st = studentsById[studentId];
            return (
              <div key={studentId} className="rounded-2xl border border-neutral-200 bg-white overflow-hidden" data-testid={`planner-student-block-${studentId}`}>
                <div className="border-b border-neutral-100 bg-neutral-50 px-4 py-2 flex items-center gap-2">
                  <User className="h-4 w-4 text-neutral-500" />
                  <div className="font-semibold text-sm">{st?.name || "Alumno desconocido"}</div>
                  <span className="text-[10px] text-neutral-500 ml-auto">{list.length} propuesta{list.length !== 1 ? "s" : ""}</span>
                </div>
                <div className="divide-y divide-neutral-100">
                  {list.map((d) => (
                    <div key={d.id} className="px-4 py-3" data-testid={`planner-draft-${d.id}`}>
                      <div className="flex items-start gap-2 flex-wrap mb-1.5">
                        <ScoreBadge score={d.score} />
                        {d.status !== "pending" && (
                          <span className={"inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold border " + statusLabels[d.status].cls}>
                            {statusLabels[d.status].label.slice(0, -1)}
                          </span>
                        )}
                        {d.distance_km != null && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-700 text-[10px] font-bold px-2 py-0.5">
                            <Route className="h-3 w-3" /> {d.distance_km} km
                          </span>
                        )}
                        {d.status === "pending" && (
                          <div className="ml-auto flex gap-1">
                            <button type="button" onClick={() => accept(d)} disabled={saving} className="inline-flex items-center gap-1 h-7 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold px-2.5 disabled:opacity-50" data-testid={`planner-accept-${d.id}`}>
                              <CheckCircle2 className="h-3 w-3" /> Aceptar
                            </button>
                            <button type="button" onClick={() => reject(d)} disabled={saving} className="btn-ghost border border-red-200 text-red-600 hover:bg-red-50 !h-7 !px-2 text-[11px]" data-testid={`planner-reject-${d.id}`}>
                              <X className="h-3 w-3" /> Rechazar
                            </button>
                          </div>
                        )}
                        {d.status !== "pending" && (
                          <button type="button" onClick={() => remove(d)} disabled={saving} className="ml-auto btn-ghost border border-neutral-200 text-neutral-500 hover:text-red-600 !h-7 !px-2 text-[11px]" data-testid={`planner-delete-${d.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 text-xs">
                        <div className="flex items-center gap-1.5"><Clock className="h-3 w-3 text-neutral-400" /><b>{d.date}</b> · {d.time} <span className="text-neutral-400">({d.duration_minutes}min)</span></div>
                        <div className="flex items-center gap-1.5"><User className="h-3 w-3 text-neutral-400" />{d.teacher_name || "—"}</div>
                        {d.zone && <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3 text-neutral-400" />{d.zone}</div>}
                      </div>
                      {d.reason && (
                        <div className="mt-1.5 text-[11px] text-neutral-600 border-l-2 border-orange-200 pl-2 italic">{d.reason}</div>
                      )}
                      {(d.availability_match === false || d.incident_conflict || (d.alerts || []).length > 0) && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {d.availability_match === false && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 text-[10px] font-bold px-2 py-0.5"><AlertTriangle className="h-3 w-3" /> Fuera disponibilidad</span>
                          )}
                          {d.incident_conflict && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-900 text-[10px] font-bold px-2 py-0.5"><Ban className="h-3 w-3" /> Incidencia activa</span>
                          )}
                          {(d.alerts || []).map((a, i) => (
                            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 text-[10px] font-bold px-2 py-0.5">{a}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
